import React, { useState, useEffect, useContext } from 'react';
import { Sparkles, CheckCircle2, AlertTriangle } from 'lucide-react';
import DashboardContext from '../../context/dashboard/DashboardContext';
import { useNavigate } from 'react-router-dom';

const STATUS_MESSAGES = [
    "Reading Documents",
    "Extracting Details",
    "Creating Itinerary",
    "Formatting Voucher",
    "Organizing Schedule",
    "Applying Theme",
    "Almost Ready"
];

const FloatingVoucherLoader = () => {
    const { analysisState, setAnalysisMinimized } = useContext(DashboardContext);
    const navigate = useNavigate();
    const [statusIndex, setStatusIndex] = useState(0);

    useEffect(() => {
        if (!analysisState.isAnalyzing) return;
        const interval = setInterval(() => {
            setStatusIndex(prev => (prev + 1) % STATUS_MESSAGES.length);
        }, 2800);
        return () => clearInterval(interval);
    }, [analysisState.isAnalyzing]);

    if (!analysisState.isAnalyzing && !analysisState.result && !analysisState.error) return null;
    if (!analysisState.isMinimized) return null;

    const progress = Math.min(100, Math.max(0, Math.round(analysisState.progress || 0)));

    const handleExpand = () => {
        setAnalysisMinimized(false);
        navigate('/create-voucher');
    };

    // Circumference for r=17 is 2 * PI * 17 ≈ 106.81
    const circumference = 106.81;
    const strokeDashoffset = circumference - (circumference * progress) / 100;

    return (
        <aside
            aria-label="Voucher generation progress"
            onClick={handleExpand}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleExpand();
                }
            }}
            role="button"
            tabIndex={0}
            title="Click anywhere to open voucher creation"
            className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[999999] cursor-pointer select-none group outline-none animate-in slide-in-from-bottom-6 fade-in duration-300"
        >
            <div className="bg-white/95 backdrop-blur-xl border border-teal-100 rounded-2xl sm:rounded-[20px] shadow-[0_12px_40px_rgba(0,0,0,0.12)] hover:shadow-[0_18px_48px_rgba(6,121,120,0.22)] hover:border-teal-300 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 ring-1 ring-teal-500/10 p-3 sm:p-3.5 flex items-center gap-3 sm:gap-3.5 min-w-[270px] sm:min-w-[295px] max-w-[340px]">
                
                {/* Circular Progress / Status Indicator */}
                <div className="relative w-10 h-10 sm:w-11 sm:h-11 flex-shrink-0 flex items-center justify-center bg-teal-50/70 rounded-full border border-teal-100/60">
                    {analysisState.isAnalyzing ? (
                        <>
                            <svg className="w-full h-full -rotate-90 p-0.5" viewBox="0 0 42 42">
                                <circle
                                    cx="21"
                                    cy="21"
                                    r="17"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                    fill="transparent"
                                    className="text-teal-100/70"
                                />
                                <circle
                                    cx="21"
                                    cy="21"
                                    r="17"
                                    stroke="#067978"
                                    strokeWidth="3"
                                    fill="transparent"
                                    strokeDasharray={circumference}
                                    strokeDashoffset={strokeDashoffset}
                                    strokeLinecap="round"
                                    className="transition-all duration-300 ease-out"
                                />
                            </svg>
                            <Sparkles className="text-[#067978] animate-pulse absolute" size={16} />
                        </>
                    ) : analysisState.error ? (
                        <div className="w-full h-full bg-red-50 rounded-full flex items-center justify-center">
                            <AlertTriangle className="text-red-500" size={20} />
                        </div>
                    ) : (
                        <div className="w-full h-full bg-emerald-50 rounded-full flex items-center justify-center">
                            <CheckCircle2 className="text-emerald-500" size={20} />
                        </div>
                    )}
                </div>

                {/* Progress Details */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1.5 mb-1">
                        <span className="text-[10px] font-bold text-slate-800 uppercase tracking-widest leading-none transition-all duration-300">
                            {analysisState.isAnalyzing ? STATUS_MESSAGES[statusIndex] : analysisState.error ? "Analysis Failed" : "Voucher Ready!"}
                        </span>
                        {analysisState.isAnalyzing && (
                            <span className="text-[11px] font-extrabold text-[#067978] leading-none">
                                {progress}%
                            </span>
                        )}
                    </div>

                    {analysisState.isAnalyzing ? (
                        <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden mt-1.5">
                            <div
                                className="h-full bg-gradient-to-r from-[#067978] to-teal-400 transition-all duration-300 rounded-full"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    ) : (
                        <p className="text-[11px] font-medium text-slate-500 mt-0.5 truncate">
                            {analysisState.error ? "Tap to check requirements" : "Click to view and edit"}
                        </p>
                    )}

                    <span className="text-[10px] text-teal-600 font-medium group-hover:underline flex items-center gap-1 mt-1">
                        Click anywhere to open
                    </span>
                </div>
            </div>
        </aside>
    );
};

export default FloatingVoucherLoader;
